// src/contexts/SubscriptionContext.jsx

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useRevenueCat } from './RevenueCatContext';
import { 
  BILLING_ENABLED, 
  BETA_CONFIG,
  getFeatureLimit, 
  hasFeatureAccess, 
  isWithinLimit,
  getTierInfo,
  getTierFeatures,
  TIERS,
  FEATURE_LIMITS,
} from '../config/subscriptionConfig';

// =====================================================
// SUBSCRIPTION CONTEXT
// =====================================================

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children, userId, bandId }) => {
  // Get RevenueCat status
  const { 
    isPro: isRevenueCatPro, 
    loading: rcLoading, 
    customerInfo,
    subscriptionInfo: rcSubscriptionInfo,
    monthlyPrice,
  } = useRevenueCat();

  // Firestore subscription data (backup/legacy)
  const [firestoreSubscription, setFirestoreSubscription] = useState({
    tier: 'free',
    status: 'active',
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    isEarlyAdopter: false,
    signupDate: null,
  });
  const [firestoreLoading, setFirestoreLoading] = useState(true);

  // Listen to subscription data from Firestore (backup)
  useEffect(() => {
    if (!bandId) {
      setFirestoreLoading(false);
      return;
    }

    const subscriptionRef = doc(db, 'bands', bandId, 'settings', 'subscription');
    
    const unsubscribe = onSnapshot(subscriptionRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setFirestoreSubscription({
          tier: data.tier || 'free',
          status: data.status || 'active',
          currentPeriodEnd: data.currentPeriodEnd?.toDate() || null,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd || false,
          isEarlyAdopter: data.isEarlyAdopter || false,
          signupDate: data.signupDate?.toDate() || null,
        });
      } else {
        // No subscription doc = free tier (this is normal)
        setFirestoreSubscription(prev => ({ ...prev, tier: 'free' }));
      }
      setFirestoreLoading(false);
    }, (error) => {
      // Permission error is expected if doc doesn't exist yet
      if (error.code === 'permission-denied') {
        setFirestoreSubscription(prev => ({ ...prev, tier: 'free' }));
      } else {
        console.error('Error fetching subscription:', error);
      }
      setFirestoreLoading(false);
    });

    return () => unsubscribe();
  }, [bandId]);

  // =====================================================
  // DETERMINE TIER (RevenueCat takes priority)
  // =====================================================
  const tier = useMemo(() => {
    // Kill switch - if billing disabled, everyone is pro
    if (!BILLING_ENABLED) return 'pro';
    
    // RevenueCat is the source of truth for paid status
    if (isRevenueCatPro) return 'pro';
    
    // Fallback to Firestore (for legacy users or if RC fails)
    if (firestoreSubscription.tier === 'pro') return 'pro';
    
    // Default to free
    return 'free';
  }, [isRevenueCatPro, firestoreSubscription.tier]);

  // Combined loading state
  const loading = rcLoading || firestoreLoading;

  // =====================================================
  // PERMISSION CHECK FUNCTIONS
  // =====================================================
  
  // Check if user has access to a feature
  const hasAccess = (feature) => {
    if (!BILLING_ENABLED) return true;
    const limit = FEATURE_LIMITS[feature]?.[tier];
    if (typeof limit === 'boolean') return limit;
    return limit > 0;
  };

  // Check usage limit for count-based features
  const checkLimit = (feature, currentUsage) => {
    if (!BILLING_ENABLED) {
      return { allowed: true, remaining: Infinity, limit: Infinity, atLimit: false };
    }

    const limit = FEATURE_LIMITS[feature]?.[tier];

    // Boolean features (true/false access)
    if (limit === true) return { allowed: true, remaining: Infinity, limit: Infinity, atLimit: false };
    if (limit === false) return { allowed: false, remaining: 0, limit: 0, atLimit: true };
    
    // Unlimited
    if (limit === Infinity) return { allowed: true, remaining: Infinity, limit: Infinity, atLimit: false };

    // Count-based limits
    const remaining = Math.max(0, limit - currentUsage);
    const atLimit = currentUsage >= limit;

    return {
      allowed: currentUsage < limit,
      remaining,
      limit,
      atLimit,
    };
  };

  // Get limit for a feature
  const getLimit = (feature) => {
    if (!BILLING_ENABLED) return Infinity;
    return FEATURE_LIMITS[feature]?.[tier] ?? 0;
  };

  // =====================================================
  // CONTEXT VALUE
  // =====================================================
  const value = {
    // Current state
    subscription: firestoreSubscription,
    loading,
    tier,
    isFreeTier: tier === 'free',
    isProTier: tier === 'pro',
    isPro: tier === 'pro',
    isPaidTier: tier !== 'free',
    
    // Beta/billing status
    billingEnabled: BILLING_ENABLED,
    isBeta: BETA_CONFIG.isBeta,
    betaEndDate: BETA_CONFIG.betaEndDate,
    
    // Tier info
    tierInfo: getTierInfo(tier),
    allTiers: TIERS,
    currentTierInfo: TIERS[tier],
    features: getTierFeatures(tier),
    
    // Pricing (from RevenueCat)
    monthlyPrice: monthlyPrice || '$8.00',
    
    // RevenueCat info
    customerInfo,
    rcSubscriptionInfo,
    
    // Permission checks
    getLimit,
    hasAccess,
    checkLimit,
    
    // Computed helpers
    isEarlyAdopter: firestoreSubscription.isEarlyAdopter,
    earlyAdopterDiscount: BETA_CONFIG.earlyAdopterDiscount,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// =====================================================
// CONVENIENCE HOOKS
// =====================================================

/**
 * Check if user has access to a specific feature
 * @param {string} feature - Feature key from FEATURE_LIMITS
 * @returns {boolean} - Whether user has access
 */
export const useFeatureAccess = (feature) => {
  const { hasAccess } = useSubscription();
  return hasAccess(feature);
};

/**
 * Check usage against limits for count-based features
 * @param {string} feature - Feature key from FEATURE_LIMITS
 * @param {number} currentUsage - Current count
 * @returns {object} - { allowed, remaining, limit, atLimit }
 */
export const useUsageCheck = (feature, currentUsage) => {
  const { checkLimit } = useSubscription();
  return checkLimit(feature, currentUsage);
};

export default SubscriptionContext;